import { useCallback, useEffect, useRef, useState } from "react";

import { endShift, fetchShiftRota, startShift } from "./supervision-api";
import { useDemoMode } from "../../lib/demo-mode";
import type { Shift, ShiftRota } from "./types";

/**
 * The caller's shift, and who else is on the floor.
 *
 * Going on shift is an action, not a side effect of rendering. The console used to begin
 * supervising the moment the page mounted, which meant a background tab counted as an
 * available supervisor and there was no honest way to divide work between people.
 *
 * The shift is kept alive by the SSE roster tick server-side, so there is no heartbeat
 * timer here -- "still streaming" and "still here" are deliberately the same signal.
 */

/** Refresh the rota while in the lobby, so arrivals and departures show up before joining. */
const LOBBY_POLL_MS = 15_000;

const DEMO_SHIFT: Shift = {
  user_id: "demo_supervisor",
  client_id: "demo_client",
  display_name: "Demo Supervisor",
  comfortable_load: 6,
  started_at: Date.now() / 1000,
  last_seen_at: Date.now() / 1000,
  permissions: ["supervision.view", "supervision.listen", "supervision.takeover"],
};

const DEMO_ROTA: ShiftRota = {
  mine: DEMO_SHIFT,
  on_shift: [DEMO_SHIFT],
  headroom: { supervisors: 1, capacity: 6, live_calls: 25, utilisation: 100 },
  handover: { live_calls: 25, open_alerts: 4, overdue_alerts: 1, held_calls: [] },
  generated_at: new Date().toISOString(),
};

export function useShift() {
  const isDemoMode = useDemoMode();
  const [rota, setRota] = useState<ShiftRota | null>(null);
  const [onShift, setOnShift] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const aliveRef = useRef(true);

  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  // In demo mode, seed the rota for the lobby so stats display before starting.
  useEffect(() => {
    if (!isDemoMode) return;
    setRota(DEMO_ROTA);
  }, [isDemoMode]);

  const refresh = useCallback(async () => {
    if (isDemoMode) {
      setRota((prev) => prev ?? DEMO_ROTA);
      return;
    }
    try {
      const next = await fetchShiftRota();
      if (!aliveRef.current) return;
      setRota(next);
      // The server is authoritative: a shift that lapsed while the tab was asleep must
      // put the operator back in the lobby rather than showing a wall they are not on.
      setOnShift(Boolean(next?.mine));
    } catch (caught) {
      if (aliveRef.current) setError((caught as Error)?.message || "Could not load the rota.");
    }
  }, [isDemoMode]);

  useEffect(() => {
    if (isDemoMode) return;
    void refresh();
  }, [refresh, isDemoMode]);

  // Only while in the lobby: once on shift the SSE snapshot carries the rota already.
  //
  // ...unless we are on shift without knowing our own entry on it. `start` sets onShift
  // before refreshing, so a rota read that failed there left `mine` null permanently --
  // and `mine.user_id` is what the wall filters the slice by, so that console silently
  // showed the whole floor for the rest of the shift. Keep asking until we know who we
  // are, then stop as designed.
  const knowsOwnShift = Boolean(rota?.mine);
  useEffect(() => {
    if (isDemoMode) return;
    if (onShift && knowsOwnShift) return;
    const timer = setInterval(() => void refresh(), LOBBY_POLL_MS);
    return () => clearInterval(timer);
  }, [isDemoMode, onShift, knowsOwnShift, refresh]);

  const start = useCallback(
    async (comfortableLoad: number) => {
      const clamped = Math.max(1, Math.min(25, comfortableLoad));
      if (isDemoMode) {
        setOnShift(true);
        const myShift: Shift = { ...DEMO_SHIFT, comfortable_load: clamped };
        setRota({
          ...DEMO_ROTA,
          mine: myShift,
          on_shift: [myShift],
          headroom: { ...DEMO_ROTA.headroom, capacity: clamped },
        });
        return;
      }
      setBusy(true);
      setError(null);
      try {
        await startShift(clamped);
        if (!aliveRef.current) return;
        setOnShift(true);
        await refresh();
      } catch (caught) {
        if (aliveRef.current) {
          setError((caught as Error)?.message || "Could not start a shift.");
        }
      } finally {
        if (aliveRef.current) setBusy(false);
      }
    },
    [isDemoMode, refresh],
  );

  const stop = useCallback(async () => {
    if (isDemoMode) {
      setOnShift(false);
      return;
    }
    setBusy(true);
    try {
      await endShift();
    } catch {
      // Coming off shift must always succeed locally. The server-side record expires on
      // its own, so a failed call costs a stale rota entry for one TTL, not a stuck console.
    } finally {
      if (aliveRef.current) {
        setOnShift(false);
        setBusy(false);
        void refresh();
      }
    }
  }, [isDemoMode, refresh]);

  return { rota, onShift, busy, error, start, stop, refresh };
}
